"use client";

import React, { useState } from "react";
import { PieChart, Pie, Sector, Cell, Tooltip, Legend, ResponsiveContainer, PieLabelRenderProps } from "recharts";

/* keep legacy palette order for visual parity */
const COLORS = ["#7cb5ec", "#434348", "#90ed7d"];

/* local helper ---------------------------------------------------------- */
const toCurrency = (n: number) =>
    `$${n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

/* highlighted‑slice renderer (typed exactly the way <Pie> expects) */
const ActiveSlice = (props: unknown) => (
    <Sector {...(props as Record<string, unknown>)} stroke="#fff" strokeWidth={3} />
);

/* ---------------------------------------------------------------------- */
/* props – all values are MONTHLY dollars                                 */
/* ---------------------------------------------------------------------- */
export interface OverTimePieProps {
    principalAndInterest: number;
    taxesAndInsurance: number;
    pmi?: number; // optional – omit / zero ⇒ slice is hidden
}

const MortgageOverTimeChart: React.FC<OverTimePieProps> = ({ principalAndInterest, taxesAndInsurance, pmi = 0 }) => {
    /* build dataset (skip PMI slice if zero) */
    const data = [
        { name: "Principal & Interest", value: principalAndInterest },
        { name: "Taxes & Insurance", value: taxesAndInsurance },
        ...(pmi > 0 ? [{ name: "PMI", value: pmi }] : []),
    ];

    const total = data.reduce((s, d) => s + d.value, 0);

    /* “hover” state for the explode‑out effect */
    const [active, setActive] = useState<number | undefined>();

    /* slice labels */
    const renderLabel = ({ value }: PieLabelRenderProps) =>
        `${toCurrency(Number(value))} (${((Number(value) / total) * 100).toFixed(1)}%)`;

    /* ------------------------------------------------------------------ */
    return (
        <div className="mt-8">
            <h3 className="text-xl font-bold text-center text-[#212459] mb-4">Monthly Payment Breakdown</h3>

            <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                    <Pie
                        data={data}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        outerRadius={150}
                        paddingAngle={4}
                        labelLine={false}
                        label={renderLabel}
                        activeIndex={active}
                        activeShape={ActiveSlice}
                        onMouseEnter={(_, i) => setActive(i)}
                        onMouseLeave={() => setActive(undefined)}
                        isAnimationActive
                        animationDuration={1000}
                    >
                        {data.map((d, i) => (
                            <Cell key={d.name} fill={COLORS[i % COLORS.length]} />
                        ))}
                    </Pie>

                    <Tooltip formatter={(v: number) => `${toCurrency(v)} (${((v / total) * 100).toFixed(1)}%)`} />

                    <Legend
                        iconType="circle"
                        align="center"
                        verticalAlign="bottom"
                        formatter={(name: string) => {
                            const slice = data.find((d) => d.name === name);
                            if (!slice) return name;
                            const pct = ((slice.value / total) * 100).toFixed(1);
                            return `${name} – ${toCurrency(slice.value)} (${pct}%)`;
                        }}
                    />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
};

export default MortgageOverTimeChart;
